import Dexie, { type DexieOptions, type Table, type Transaction } from "dexie";

import type {
  AppMetadata,
  AppSettings,
  BackupMetadata,
  Collection,
  DuaReflection,
  DuaSession,
  MemorisationState,
  Person,
  PersonalDua,
  SessionBlock,
  Tag,
} from "../domain";
import { normalizeLabel } from "../domain";

export const DATABASE_NAME = "dua-workspace";
export const CURRENT_DATABASE_VERSION = 2;

export const V1_STORES = {
  personalDuas:
    "&id, status, focusLevel, favorite, createdAt, updatedAt, deletedAt, lastReviewedAt, *collectionIds, *tagIds, *personIds, sourceLibraryEntryId",
  collections: "&id, name, sortOrder, archivedAt, updatedAt, deletedAt",
  tags: "&id, name, sortOrder, updatedAt, deletedAt",
  people: "&id, displayName, relationship, lastRememberedAt, sortOrder, updatedAt, deletedAt",
  reflections: "&id, duaId, type, occurredAt, createdAt, updatedAt, deletedAt",
  sessions: "&id, type, sortOrder, archivedAt, updatedAt, deletedAt",
  sessionBlocks: "&id, sessionId, type, sortOrder, updatedAt, deletedAt",
  memorisationStates:
    "&id, &[targetType+targetId], status, nextReviewAt, lastReviewedAt, updatedAt, deletedAt",
  settings: "&id, updatedAt",
  backupMetadata: "&id, updatedAt, lastSuccessfulBackupAt",
  appMetadata: "&id, updatedAt",
} as const;

export const V2_STORES = {
  personalDuas:
    "&id, status, focusLevel, favorite, createdAt, updatedAt, deletedAt, lastReviewedAt, [status+updatedAt], *collectionIds, *tagIds, *personIds, *sessionIds, *contextTags, sourceLibraryEntryId",
  collections: "&id, normalizedName, sortOrder, archivedAt, updatedAt, deletedAt",
  tags: "&id, normalizedName, sortOrder, updatedAt, deletedAt",
  people:
    "&id, normalizedName, relationship, lastRememberedAt, sortOrder, archivedAt, updatedAt, deletedAt",
  reflections: "&id, duaId, type, occurredAt, createdAt, updatedAt, deletedAt",
  sessions: "&id, type, sortOrder, archivedAt, updatedAt, deletedAt",
  sessionBlocks: "&id, sessionId, type, [sessionId+sortOrder], sortOrder, updatedAt, deletedAt",
  memorisationStates:
    "&id, &[targetType+targetId], status, nextReviewAt, lastReviewedAt, updatedAt, deletedAt",
  settings: "&id, updatedAt",
  backupMetadata: "&id, updatedAt, lastSuccessfulBackupAt",
  appMetadata: "&id, updatedAt",
} as const;

interface LegacyPersonalDua extends Partial<PersonalDua> {
  id: string;
}

interface LegacyNamedRecord {
  name?: unknown;
  normalizedName?: unknown;
}

interface LegacyPersonRecord {
  displayName?: unknown;
  normalizedName?: unknown;
  archivedAt?: unknown;
}

async function migrateV1ToV2(transaction: Transaction): Promise<void> {
  await transaction
    .table<LegacyPersonalDua, string>("personalDuas")
    .toCollection()
    .modify((dua) => {
      dua.sessionIds ??= [];
      dua.contextTags ??= [];
      dua.collectionIds ??= [];
      dua.tagIds ??= [];
      dua.personIds ??= [];
      dua.favorite ??= false;
      dua.reviewCount ??= 0;
    });

  await Promise.all([
    transaction
      .table<LegacyNamedRecord, string>("collections")
      .toCollection()
      .modify((collection) => {
        if (typeof collection.name === "string") {
          collection.normalizedName = normalizeLabel(collection.name);
        }
      }),
    transaction
      .table<LegacyNamedRecord, string>("tags")
      .toCollection()
      .modify((tag) => {
        if (typeof tag.name === "string") {
          tag.normalizedName = normalizeLabel(tag.name);
        }
      }),
    transaction
      .table<LegacyPersonRecord, string>("people")
      .toCollection()
      .modify((person) => {
        if (typeof person.displayName === "string") {
          person.normalizedName = normalizeLabel(person.displayName);
        }
      }),
  ]);

  await transaction
    .table<Partial<AppMetadata>, string>("appMetadata")
    .toCollection()
    .modify((metadata) => {
      metadata.databaseVersion = CURRENT_DATABASE_VERSION;
      metadata.bootstrapVersion ??= 0;
    });
}

export class DuaWorkspaceDatabase extends Dexie {
  declare personalDuas: Table<PersonalDua, string>;
  declare collections: Table<Collection, string>;
  declare tags: Table<Tag, string>;
  declare people: Table<Person, string>;
  declare reflections: Table<DuaReflection, string>;
  declare sessions: Table<DuaSession, string>;
  declare sessionBlocks: Table<SessionBlock, string>;
  declare memorisationStates: Table<MemorisationState, string>;
  declare settings: Table<AppSettings, string>;
  declare backupMetadata: Table<BackupMetadata, string>;
  declare appMetadata: Table<AppMetadata, string>;

  constructor(name: string = DATABASE_NAME, options?: DexieOptions) {
    super(name, options);

    this.version(1).stores(V1_STORES);
    this.version(CURRENT_DATABASE_VERSION).stores(V2_STORES).upgrade(migrateV1ToV2);
  }
}

export const db = new DuaWorkspaceDatabase();
